import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Clock, ChevronRight, ChevronLeft, Edit, Save, Trash, ThumbsUp, ThumbsDown, Star, Play, X, Move, ArrowUp, ArrowDown, CheckCircle, MessageSquare, Bookmark, Share2, FileText, Eye, Camera, Shield, ExternalLink, Search } from 'lucide-react';
import { parseEvents } from "../TIMEBAR/parseEvents";
import { getRobotEvents } from "../TIMEBAR/robotEvents";
import { processEventsForGrouping } from "../TIMEBAR/EventGroupHelper";
import { useEventContext } from "../TIMEBAR/EventContext";
import { useSharedEvents } from "../TIMEBAR/SharedEventsContext";
import './StoryboardOverview.css';
import { useAppStore } from "../../store/appStore";

const StoryboardOverview = ({ onEventSelect, onSaveEvent, onPlayVideo }) => {
  const [allEvents, setAllEvents] = useState([]);
  const [storyboardEvents, setStoryboardEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState("11AM"); // Default to day shift
  const [showAnnotation, setShowAnnotation] = useState(null);
  const [annotations, setAnnotations] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null); // null means show all priorities
  const [sortBy, setSortBy] = useState("time");
  const [interestMarks, setInterestMarks] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [playingCard, setPlayingCard] = useState(null);
  const [videoUrls, setVideoUrls] = useState({});
  const [selectedType, setSelectedType] = useState("all"); // "all", "vehicle", "people"
  // State to track which counts have updated
  const [updatedCounts, setUpdatedCounts] = useState({
    priority: null,
    type: null
  });
  
  // Get the updateVideoState function from the app store
  const updateVideoState = useAppStore(state => state.updateVideoState);
  // Enhanced state for evidence workflow
  const [activeWorkflowPanel, setActiveWorkflowPanel] = useState(null);
  const [currentEventData, setCurrentEventData] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [evidenceCategory, setEvidenceCategory] = useState("uncategorized");
  // const [evidenceImportance, setEvidenceImportance] = useState("medium");
  const [actionRequired, setActionRequired] = useState("monitor"); // 默认 Monitor & Update

  const [shareMessage, setShareMessage] = useState("");
  const [reviewStatus, setReviewStatus] = useState({});

  // Event data for modals
  const [modalData, setModalData] = useState({
    isOpen: false,
    type: null, // 'note', 'share', 'review'
    eventId: null,
    content: ""
  });
  
  const eventsPerPage = 24; // 每页显示24个事件
  const storyboardRef = useRef(null);
  
  // Context for handling event selection
  const { setActiveEvent } = useEventContext();
  const { addSharedEvent, addToWorkspace } = useSharedEvents();
  
  // Priority colors and labels
  const priorityColors = {
    1: "#bd0026", // Emergency - Red
    2: "#f03b20", // Urgent - Orange
    3: "#fd8d3c", // Moderate - Light Orange
    4: "#90EE90", // Advisory - Light Green
  };
  
  const getPriorityLabel = (priority) => {
    switch(priority) {
      case 1: return "Emergency";
      case 2: return "Urgent";
      case 3: return "Moderate";
      case 4: return "Advisory";
      default: return "Moderate";
    }
  };
  
  // Event type labels
  const eventLabels = {
    1: "Civil-Vehicle",
    2: "Civil-Person",
    3: "Civil-Other",
    4: "Crime-Vehicle",
    5: "Crime-Person",
    6: "Crime-Other",
  };
  // Format time
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };
  
  // Create timestamp
  const createTimeStamp = (timeframe, startTime) => {
    const isDaytime = timeframe === "11AM";
    const minutes = Math.floor(startTime / 60);
    const seconds = Math.floor(startTime % 60);
    
    let hour = isDaytime ? 11 : 23;
    let finalMinutes = minutes;
    
    hour += Math.floor(finalMinutes / 60);
    finalMinutes = finalMinutes % 60;
    
    const formattedHour = hour > 12 ? `${hour - 12}` : `${hour}`;
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedMinutes = finalMinutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');
    
    return `${formattedHour}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
  };
  
  // Calculate frame size based on priority
  const getFrameSize = (priority) => {
    return 'standard'; // 返回一个固定值
  };
  
  // Get event icon
  const getEventIcon = (type) => {
    const typeNum = Number(type);
    switch(typeNum) {
      case 1: return <img src="/smallicons/important_car_small.png" alt="Civil-Vehicle" width="18" height="18" />;
      case 2: return <img src="/smallicons/important_people_small.png" alt="Civil-Person" width="18" height="18" />;
      case 3: return <img src="/smallicons/important_others_small.png" alt="Civil-Other" width="18" height="18" />;
      case 4: return <img src="/smallicons/urgent_car_small.png" alt="Crime-Vehicle" width="18" height="18" />;
      case 5: return <img src="/smallicons/urgent_people_small.png" alt="Crime-Person" width="18" height="18" />;
      case 6: return <img src="/smallicons/urgent_others_small.png" alt="Crime-Other" width="18" height="18" />;
      default: return <AlertCircle size={18} />;
    }
  };
  
  // Enhanced priority count that considers all active filters
  const getDynamicPriorityCount = (priority) => {
    // Apply type filtering first if a type filter is active
    let filteredByType = allEvents.filter(event => event.timeframe === selectedTimeframe);
    
    if (selectedType === "vehicle") {
      filteredByType = filteredByType.filter(event => 
        event.type === 1 || event.type === 4
      );
    } else if (selectedType === "people") {
      filteredByType = filteredByType.filter(event => 
        event.type === 2 || event.type === 5
      );
    }
    
    // Then count events matching the specified priority
    return filteredByType.filter(event => 
      (event.priority || 3) === priority
    ).length;
  };

  // Enhanced type count that considers active priority filter
  const getDynamicTypeCount = (type) => {
    // Apply priority filtering first if a priority filter is active
    let filteredByPriority = allEvents.filter(event => 
      event.timeframe === selectedTimeframe
    );
    
    if (selectedPriority !== null) {
      filteredByPriority = filteredByPriority.filter(event => 
        (event.priority || 3) === selectedPriority
      );
    }
    
    // Then apply the type filter for counting
    if (type === "all") {
      return filteredByPriority.length;
    } else if (type === "vehicle") {
      return filteredByPriority.filter(event => 
        event.type === 1 || event.type === 4
      ).length;
    } else if (type === "people") {
      return filteredByPriority.filter(event => 
        event.type === 2 || event.type === 5
      ).length;
    }
    
    return 0;
  };

  // Handle timeframe selection
  const handleTimeframeSelect = (timeframe) => {
    setLoading(true);
    setSelectedTimeframe(timeframe);
  };
  
  // Handle priority selection
  const handlePrioritySelect = (priority) => {
    // If the same priority is selected again, toggle it off (select all)
    setSelectedPriority(selectedPriority === priority ? null : priority);
    
    // Mark counts as updated for animation
    setUpdatedCounts({
      priority: priority,
      type: selectedType
    });
    
    // Reset after animation completes
    setTimeout(() => {
      setUpdatedCounts({
        priority: null,
        type: null
      });
    }, 500);
  };
  
  // Handle type selection
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    
    // Mark counts as updated for animation
    setUpdatedCounts({
      priority: selectedPriority,
      type: type
    });
    
    // Reset after animation completes
    setTimeout(() => {
      setUpdatedCounts({
        priority: null,
        type: null
      });
    }, 500);
  };
  
  // Handle sort change
  const handleSortChange = (newSortOption) => {
    setSortBy(newSortOption);
  };
  
  // Open modal panel
  const openModal = (type, event) => {
    // Generate unique event ID
    const eventId = `${event.robotId}-${event.timeframe}-${event.startTime}`;
    
    // Set current event data
    setCurrentEventData(event);
    
    // Clear previous inputs
    setNoteContent("");
    setEvidenceCategory("uncategorized");
    setActionRequired("monitor");
    setShareMessage("");
    
    // Open the modal
    setModalData({
      isOpen: true,
      type,
      eventId,
      content: ""
    });
  };

  // Close modal
  const closeModal = () => {
    setModalData({
      isOpen: false,
      type: null,
      eventId: null,
      content: ""
    });
    setCurrentEventData(null);
  };
  
  // Save to workspace
  const saveToWorkspace = () => {
    if (!currentEventData) return;
    
    // Ensure the event has an ID
    if (!currentEventData.id) {
      currentEventData.id = `${currentEventData.robotId}-${currentEventData.startTime}-${Date.now()}`;
    }
    
    // Create note object
    const note = {
      id: Date.now().toString(),
      content: noteContent,
      timestamp: new Date().toISOString(),
      isImportant: actionRequired === "dispatch",
      category: evidenceCategory
    };
    
    // Get existing savedTo values or create new array
    const existingSavedTo = Array.isArray(currentEventData.savedTo) ? 
      [...currentEventData.savedTo] : 
      (currentEventData.savedTo ? [currentEventData.savedTo] : []);
    
    // Add 'workspace' if not already included
    if (!existingSavedTo.includes('workspace')) {
      existingSavedTo.push('workspace');
    }
    
    // Prepare event data to save
    const eventToSave = {
      ...currentEventData,
      notes: [note],
      timestamp: new Date().toISOString(),
      savedTo: existingSavedTo,
      evidenceCategory,
      actionRequired,
      viewed: false
    };
    
    // Add to workspace via context
    if (addToWorkspace) {
      addToWorkspace(eventToSave);
    } else {
      addSharedEvent(eventToSave);
    }
    
    // If external handler exists, call it
    if (onSaveEvent) {
      onSaveEvent(eventToSave);
    }
    
    // Update review status
    setReviewStatus(prev => ({
      ...prev,
      [modalData.eventId]: {
        ...prev[modalData.eventId],
        saved: true,
        savedType: "workspace"
      }
    }));
    
    console.log("Event saved to workspace:", eventToSave);
    
    // Close modal
    closeModal();
  };

  // Share with team
  const shareWithTeam = () => {
    if (!currentEventData) return;
    
    // Ensure the event has an ID
    if (!currentEventData.id) {
      currentEventData.id = `${currentEventData.robotId}-${currentEventData.startTime}-${Date.now()}`;
    }
    
    // Create note/message object
    const message = {
      id: Date.now().toString(),
      sender: "Current Officer", // Replace with actual user name
      message: shareMessage,
      timestamp: new Date().toISOString()
    };
    
    // Get existing savedTo values or create new array
    const existingSavedTo = Array.isArray(currentEventData.savedTo) ? 
      [...currentEventData.savedTo] : 
      (currentEventData.savedTo ? [currentEventData.savedTo] : []);
    
    // Add 'shared' if not already included
    if (!existingSavedTo.includes('shared')) {
      existingSavedTo.push('shared');
    }
    
    // Prepare event data to share
    const eventToShare = {
      ...currentEventData,
      messages: [message],
      timestamp: new Date().toISOString(),
      savedTo: existingSavedTo,
      shareMessage: shareMessage,
      actionRequired: actionRequired,
      viewed: false
    };
    
    // Add to shared events via context
    addSharedEvent(eventToShare);
    
    // Update review status
    setReviewStatus(prev => ({
      ...prev,
      [modalData.eventId]: {
        ...prev[modalData.eventId],
        shared: true
      }
    }));
    
    console.log("Event shared with team:", eventToShare);
    
    // Close modal
    closeModal();
  };
  
  // Mark as reviewed
  const markAsReviewed = (event, e) => {
    e.stopPropagation();
    const eventId = `${event.robotId}-${event.timeframe}-${event.startTime}`;
    
    setReviewStatus(prev => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        reviewed: !prev[eventId]?.reviewed
      }
    }));
  };
  
  // Handle event click
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    
    // Jump to the event in the video/timeline
    if (onEventSelect) {
      onEventSelect(event.startTime, event.robotId);
    }
    
    // Use context to activate this event
    setActiveEvent(null, event.robotId, event.startTime);
  };
  
  // Play video
  const handlePlayVideo = (event, e) => {
    e.stopPropagation();
    const eventId = `${event.robotId}-${event.timeframe}-${event.startTime}`;
    
    if (playingCard === eventId) {
      setPlayingCard(null);
    } else {
      setPlayingCard(eventId);
      
      // Get the video element
      const videoElement = document.querySelector(`video[data-event-id="${eventId}"]`);
      if (videoElement) {
        // Set the start time to the event's start time
        videoElement.currentTime = event.startTime;
        // Play the video
        videoElement.play();
      }
      
      if (onPlayVideo) {
        onPlayVideo(event.robotId, event.startTime, event.timeframe);
      }
    }
  };
  
  // New function for Go Details button
  // Modified handleGoDetailsClick function to use updateVideoState directly
  const handleGoDetailsClick = (event, e) => {
    e.stopPropagation(); // Prevent the card click event
    
    // Generate timePart based on timeframe
    const timePart = event.timeframe === "11AM" ? "day" : "night";
    const videoUrl = `/${timePart}/videos/video${event.robotId}.mp4`;
    
    console.log("Go Details clicked for event:", event);
    
    // Use the updateVideoState method directly from the store
    // This is the key change - mimic exactly what GenericRobotEvents.js does
    try {
      updateVideoState({
        robotId: event.robotId,
        eventId: null, // We don't have an event index here, so passing null
        videoUrl,
        time: event.startTime,
        isPlaying: true,
        isHls: false,
        hlsResumeTime: null,
        hlsBaseTime: 0,
        currentEvent: event
      }, 'timeline_event_click');
      
      console.log(`Go Details: Video state updated, jumping to time: ${event.startTime}s`);
    } catch (err) {
      console.error(`Go Details: Error updating video state:`, err);
    }
    
    // Also call onEventSelect as a backup
    if (onEventSelect) {
      onEventSelect(event.startTime, event.robotId);
    }
    
    // And update context as a backup
    setActiveEvent(null, event.robotId, event.startTime);
  };

  const getCriticalEventNames = () => {
    // Get critical events that match current filters
    let criticalEvents = allEvents.filter(event => 
      (event.priority || 3) === 1 && 
      event.timeframe === selectedTimeframe
    );
    
    // Apply type filtering if a type filter is active
    if (selectedType === "vehicle") {
      criticalEvents = criticalEvents.filter(event => 
        event.type === 1 || event.type === 4
      );
    } else if (selectedType === "people") {
      criticalEvents = criticalEvents.filter(event => 
        event.type === 2 || event.type === 5
      );
    }
    
    if (criticalEvents.length === 0) return "";
    if (criticalEvents.length === 1) return criticalEvents[0].event;
    if (criticalEvents.length === 2) 
      return `${criticalEvents[0].event} and ${criticalEvents[1].event}`;
    
    return `${criticalEvents[0].event}, ${criticalEvents[1].event}, and ${criticalEvents.length - 2} more`;
  };
  
  const getTimeOfDay = (timeframe) => {
    return timeframe.includes("AM") ? "Day" : "Night";
  };
  
  // Helper function to format the displayed time
  const getDisplayTime = (timeframe) => {
    return timeframe.includes("AM") ? timeframe : "9:30PM";
  };
  
  // Load events
  useEffect(() => {
    const loadAllEvents = async () => {
      setLoading(true);
      
      const uniqueEvents = new Set();
      const allLoadedEvents = [];
      
      // Only load events for the selected timeframe
      const timeframe = selectedTimeframe;
      
      for (let robotId = 1; robotId <= 20; robotId++) {
        const csvContent = getRobotEvents(robotId, timeframe);
        
        if (csvContent) {
          parseEvents(csvContent, (parsedEvents) => {
            parsedEvents.forEach((event) => {
              if (!event.confidence) {
                if (event.probability) {
                  const prob = parseFloat(event.probability);
                  if (!isNaN(prob)) {
                    if (prob >= 0.7) event.confidence = "high";
                    else if (prob >= 0.4) event.confidence = "medium";
                    else event.confidence = "low";
                  } else {
                    event.confidence = "medium";
                  }
                }
              }
              
              event.event = (event.event || "").replace(/\(\d+\)/, "").trim();
              event.robotId = robotId;
              event.timeframe = timeframe;
            });
            
            const groupedEvents = processEventsForGrouping(parsedEvents);
            
            groupedEvents.forEach(event => {
              const eventKey = `${event.robotId}-${event.timeframe}-${event.startTime}-${event.event}`;
              if (!uniqueEvents.has(eventKey)) {
                uniqueEvents.add(eventKey);
                allLoadedEvents.push(event);
              }
            });
          }, 0, timeframe);
        }
      }
  
      // Sort events
      if (sortBy === "time") {
        allLoadedEvents.sort((a, b) => a.startTime - b.startTime);
      } else if (sortBy === "urgency") {
        allLoadedEvents.sort((a, b) => {
          if ((a.priority || 3) !== (b.priority || 3)) {
            return (a.priority || 3) - (b.priority || 3);
          }
          return a.startTime - b.startTime;
        });
      }
      
      // Store all events first (for dynamic count calculations)
      setAllEvents(allLoadedEvents);
      
      // Apply type filtering if needed
      let filteredEvents = allLoadedEvents;
      if (selectedType === "vehicle") {
        filteredEvents = allLoadedEvents.filter(event => 
          event.type === 1 || event.type === 4
        );
      } else if (selectedType === "people") {
        filteredEvents = allLoadedEvents.filter(event => 
          event.type === 2 || event.type === 5
        );
      }
      
      // Apply priority filtering if needed
      if (selectedPriority !== null) {
        filteredEvents = filteredEvents.filter(event => 
          (event.priority || 3) === selectedPriority
        );
      }
      
      setStoryboardEvents(filteredEvents);
      setLoading(false);
    };
    
    loadAllEvents();
  }, [selectedTimeframe, selectedType, sortBy, selectedPriority]);

  // Load video URLs
  useEffect(() => {
    // 加载视频URL的映射表
    const loadVideoMappings = () => {
      // 这里需要按时间段获取视频URLs
      const dayVideos = [
        "/day/videos/video1.mp4",
        "/day/videos/video2.mp4",
        "/day/videos/video3.mp4",
        "/day/videos/video4.mp4",
        "/day/videos/video5.mp4",
        "/day/videos/video6.mp4",
        "/day/videos/video7.mp4",
        "/day/videos/video8.mp4",
        "/day/videos/video9.mp4",
        "/day/videos/video10.mp4",
      ];
      
      const nightVideos = [
        "/night/videos/video1.mp4",
        "/night/videos/video2.mp4",
        "/night/videos/video3.mp4",
        "/night/videos/video4.mp4",
        "/night/videos/video5.mp4",
        "/night/videos/video6.mp4",
        "/night/videos/video7.mp4",
        "/night/videos/video8.mp4",
        "/night/videos/video9.mp4",
        "/night/videos/video10.mp4",
      ];
      
      // 创建机器人ID到视频URL的映射
      const urlMapping = {};
      
      for (let i = 1; i <= 10; i++) {
        urlMapping[`${i}-11AM`] = dayVideos[i-1];
        urlMapping[`${i}-11PM`] = nightVideos[i-1];
      }
      
      setVideoUrls(urlMapping);
    };
    
    loadVideoMappings();
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  
  // Filter events based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(storyboardEvents);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = storyboardEvents.filter(event => {
      const eventName = (event.event || "").toLowerCase();
      const eventType = (eventLabels[event.type || 1] || "").toLowerCase();
      const priority = getPriorityLabel(event.priority || 3).toLowerCase();
      const robotId = `robot ${event.robotId}`.toLowerCase();
      const time = createTimeStamp(event.timeframe, event.startTime).toLowerCase();

      return eventName.includes(query) || 
             eventType.includes(query) || 
             priority.includes(query) ||
             robotId.includes(query) ||
             time.includes(query);
    });

    setSearchResults(filtered);
  }, [searchQuery, storyboardEvents]);

  return (
    <div className="storyboard-container">
      {/* Header with overview summary */}
      <div className="storyboard-overview">
        <div className="overview-header">
          <div className="overview-subtitle">
            <p>
              <span className="evidence-count">{allEvents.length}</span> events detected across campus on <span style={{ color: '#3b82f6' }}>{getTimeOfDay(selectedTimeframe)}-{getDisplayTime(selectedTimeframe)}</span> from 10 surveillance robots on GMU campus. 
              <span className="critical-evidence-alert"> {getDynamicPriorityCount(1)} events: {getCriticalEventNames()} require immediate attention</span> based on priority assessment.
              {selectedPriority !== null && (
                <span className="filter-summary"> Showing <strong>{getPriorityLabel(selectedPriority)}</strong> priority events ({storyboardEvents.length} total). </span>
              )}
              {selectedType !== "all" && (
                <span className="filter-summary"> Filtered to <strong>{selectedType}-related</strong> events. </span>
              )}
              Use filters below to review evidence.
            </p>
          </div>
        </div>

{/* Add search bar */}
<div className="search-bar">
          <div className="search-input-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search events by name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search" 
                onClick={() => setSearchQuery("")}
              >
                <X size={16} />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="search-results-count">
              Found {searchResults.length} events
            </div>
          )}
        </div>
        
        <div className="overview-filters">
          <div className="filter-header">
            <div className="filter-groups">
              <div className="filter-group">
                <span className="filter-label">Shift:</span>
                <button 
                  className={`filter-btn ${selectedTimeframe === "11AM" ? 'active' : ''}`}
                  onClick={() => handleTimeframeSelect("11AM")}
                >
                  <Clock size={16} />
                  <span>Day Shift</span>
                </button>
                <button 
                  className={`filter-btn ${selectedTimeframe === "11PM" ? 'active' : ''}`}
                  onClick={() => handleTimeframeSelect("11PM")}
                >
                  <Clock size={16} />
                  <span>Night Shift</span>
                </button>
              </div>
              <div className="filter-group">
                <span className="filter-label">Priority:</span>
                {[1, 2, 3, 4].map(priority => (
                  <button 
                    key={priority}
                    className={`filter-btn ${selectedPriority === priority ? 'active' : ''} ${updatedCounts.priority === priority ? 'count-updated' : ''}`}
                    onClick={() => handlePrioritySelect(priority)}
                    style={{ 
                      borderColor: priorityColors[priority],
                      backgroundColor: selectedPriority === priority ? priorityColors[priority] : 'transparent'
                    }}
                  >
                    {getPriorityLabel(priority)} ({getDynamicPriorityCount(priority)})
                  </button>
                ))}
              </div>

              <div className="filter-group">
  <span className="filter-label">Type:</span>
  <button 
    className={`filter-btn ${selectedType === "all" ? 'active' : ''} ${updatedCounts.type === "all" ? 'count-updated' : ''}`}
    onClick={() => handleTypeSelect("all")}
  >
    All Events ({getDynamicTypeCount("all")})
  </button>
  <button 
    className={`filter-btn ${selectedType === "vehicle" ? 'active' : ''} ${updatedCounts.type === "vehicle" ? 'count-updated' : ''}`}
    onClick={() => handleTypeSelect("vehicle")}
  >
    Vehicle Related ({getDynamicTypeCount("vehicle")})
  </button>
  <button 
    className={`filter-btn ${selectedType === "people" ? 'active' : ''} ${updatedCounts.type === "people" ? 'count-updated' : ''}`}
    onClick={() => handleTypeSelect("people")}
  >
    People Related ({getDynamicTypeCount("people")})
  </button>
</div>

            </div>

            {/* <div className="sort-by">
              <span className="filter-label">Sort by:</span>
              <button 
                className={`filter-btn ${sortBy === "time" ? 'active' : ''}`}
                onClick={() => handleSortChange("time")}
              >
                <Clock size={16} />
                <span>Time</span>
              </button>
              <button 
                className={`filter-btn ${sortBy === "urgency" ? 'active' : ''}`}
                onClick={() => handleSortChange("urgency")}
              >
                <AlertCircle size={16} />
                <span>Priority</span>
              </button>
            </div> */}
          </div>
        </div>
      </div>
      {/* Loading state */}
      {loading ? (
        <div className="storyboard-loading">
          <div className="storyboard-loading-spinner"></div>
          <p>Processing surveillance footage...</p>
        </div>
      ) : (
        <>
          {/* Evidence Grid - Use searchResults instead of storyboardEvents */}
          <div className="storyboard-grid" ref={storyboardRef}>
            {searchResults
              .filter(event => 
                (selectedPriority === null || (event.priority || 3) === selectedPriority)
              )
              .map((event, index) => {
                const eventId = `${event.robotId}-${event.timeframe}-${event.startTime}`;
                const eventStatus = reviewStatus[eventId] || {};
                
                return (
                  <div 
                    key={index}
                    className={`storyboard-frame ${selectedEvent === event ? 'selected' : ''} ${eventStatus.reviewed ? 'reviewed' : ''}`}
                    onClick={() => handleEventClick(event)}
                    style={{ 
                      borderColor: priorityColors[event.priority || 3] 
                    }}
                  >
                    {/* Frame header */}
                    <div className="frame-header">
                      <div className="frame-title">
                        <div className="frame-icon">
                          {getEventIcon(event.type || 1)}
                        </div>
                        <div className="frame-info">
                          <div className="frame-label">{eventLabels[event.type || 1]}</div>
                          <div className="frame-priority" style={{ color: priorityColors[event.priority || 3] }}>
                            {getPriorityLabel(event.priority || 3)}
                          </div>
                        </div>
                      </div>
                      <div className="frame-time">
                        {/* <Clock size={14} /> */}
                        {createTimeStamp(event.timeframe, event.startTime)}
                      </div>
                    </div>
                    
                    {/* Frame content - image or video */}
                    
  <div className="frame-image-container">
    {playingCard === eventId ? (
      <video 
        src={videoUrls[`${event.robotId}-${event.timeframe}`] || `/api/placeholder/400/400?text=Robot ${event.robotId} Video`}
        className="frame-video"
        data-event-id={eventId}
        autoPlay 
        controls
        controlsList="nofullscreen" // Add this attribute to disable fullscreen button
        onEnded={() => setPlayingCard(null)}
        onLoadedMetadata={(e) => {
          if (playingCard === eventId) {
            e.target.currentTime = event.startTime;
            e.target.play();
          }
        }}
      >
        Your browser does not support HTML5 video
      </video>
    ) : (
      <>
        <img 
          src={event.imagePath || `/api/placeholder/400/400?text=Robot ${event.robotId}`}
          alt={event.event}
          className="frame-image"
        />
        
        {/* Video play button */}
        <div className="play-video-overlay">
          <button 
            className="play-video-button"
            onClick={(e) => handlePlayVideo(event, e)}
            aria-label="Play video"
          >
            <Play size={30} />
          </button>
        </div>
        
        {/* Status indicators */}
        <div className="frame-status-indicators">
          {eventStatus.reviewed && (
            <div className="status-indicator reviewed">
              <Eye size={14} />
            </div>
          )}
          {eventStatus.saved && (
            <div className="status-indicator saved">
              <Bookmark size={14} />
            </div>
          )}
          {eventStatus.shared && (
            <div className="status-indicator shared">
              <Share2 size={14} />
            </div>
          )}
        </div>
      </>
    )}
  </div>

  {/* Robot badge now outside of the image container */}
 {/* Robot badge with Go Details button */}
<div className="frame-robot-badge">
  <span>Robot {event.robotId}</span>
  <button 
    className="go-details-btn"
    onClick={(e) => handleGoDetailsClick(event, e)}
    title="Go to details view"
  >
    <ExternalLink size={12} />
    <span>Check Details</span>
  </button>
</div>


                    
                    {/* Frame content */}
                    <div className="frame-content">
                      <div className="frame-event-info">
                        <div className="frame-event-name">{event.event}</div>
                      </div>
                    </div>
                    
                    {/* Frame footer with action buttons */}
                    <div className="frame-footer">
                      <div className="frame-actions">
                        <button 
                          className={`police-action-btn review ${eventStatus.reviewed ? 'active' : ''}`}
                          onClick={(e) => markAsReviewed(event, e)}
                          title={eventStatus.reviewed ? "Mark as unreviewed" : "Mark as reviewed"}
                        >
                          <Eye size={14} />
                          {eventStatus.reviewed ? "Reviewed" : "Review"}
                        </button>
                        <button 
                          className="police-action-btn save"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal('note', event);
                          }}
                          title="Add to workspace with notes"
                        >
                          <Bookmark size={14} />
                          Save
                        </button>
                        <button 
                          className="police-action-btn share"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModal('share', event);
                          }}
                          title="Share with team"
                        >
                          <Share2 size={14} />
                          Share
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          
          {/* Pagination */}
          <div className="storyboard-navigation">
            <button 
              className="nav-btn prev-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            >
              <ChevronLeft size={20} />
            </button>
            <div className="nav-info">
              <span>Showing {searchResults.filter(event => 
                (selectedPriority === null || (event.priority || 3) === selectedPriority)
              ).length} events</span>
            </div>
            <button 
              className="nav-btn next-btn"
              disabled={currentPage * eventsPerPage >= searchResults.filter(event => 
                (selectedPriority === null || (event.priority || 3) === selectedPriority)
              ).length}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </>
      )}

{/* Modals */}
      {modalData.isOpen && (
        <div className="storyboard-modal-overlay">
          <div className="storyboard-modal">
            <div className="modal-header">
              <h3>
                {modalData.type === 'note' && "Add to Workspace"}
                {modalData.type === 'share' && "Share with Team"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={18} />
              </button>
            </div>
            
            <div className="modal-content">
              {/* Event preview */}
              {currentEventData && (
                <div className="modal-event-preview">
                  <div className="preview-image">
                    <img 
                      src={currentEventData.imagePath || `/api/placeholder/200/200?text=Robot ${currentEventData.robotId}`}
                      alt={currentEventData.event}
                    />
                  </div>
                  <div className="preview-info">
                    <h4>{currentEventData.event}</h4>
                    <div className="preview-details">
                      <span className="preview-type">
                        {eventLabels[currentEventData.type || 1]}
                      </span>
                      <span className="preview-time">
                        <Clock size={14} />
                        {createTimeStamp(currentEventData.timeframe, currentEventData.startTime)}
                      </span>
                      <span className="preview-unit">
                        <Camera size={14} />
                       Robot {currentEventData.robotId}
                      </span>
                      <span 
                        className="preview-priority"
                        style={{ color: priorityColors[currentEventData.priority || 3] }}
                      >
                        <AlertCircle size={14} />
                        {getPriorityLabel(currentEventData.priority || 3)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Note form */}
              {modalData.type === 'note' && (
                <div className="modal-form">
                  <div className="form-group">
                    <label>Add Notes</label>
                    <textarea
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      placeholder="Add details about this evidence..."
                      rows={4}
                    />
                  </div>
                </div>
              )}
              
              {/* Share form */}
              {modalData.type === 'share' && (
                <div className="modal-form">
                  <div className="form-group">
                    <label>Message for Team</label>
                    <textarea
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      placeholder="Add context or instructions for your team..."
                      rows={4}
                    />
                  </div>
                  
                  <div className="form-row">
                  <div className="form-group">
  <label>Action Required</label>
  <select 
    value={actionRequired}
    onChange={(e) => setActionRequired(e.target.value)}
  >
    <option value="dispatch">Immediate Dispatch</option>
    <option value="investigate">Investigate ASAP</option>
    <option value="monitor">Monitor & Update</option>
    <option value="reference">For Reference Only</option>
  </select>
</div>

                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={closeModal}>
                Cancel
              </button>
              
              {modalData.type === 'note' && (
                <button 
                  className="modal-btn save" 
                  onClick={saveToWorkspace}
                  disabled={!noteContent.trim()}
                >
                  <Bookmark size={16} />
                  Save to Workspace
                </button>
              )}
              
              {modalData.type === 'share' && (
                <button 
                  className="modal-btn share" 
                  onClick={shareWithTeam}
                  disabled={!shareMessage.trim()}
                >
                  <Share2 size={16} />
                  Share with Team
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryboardOverview;